import React, { useState } from 'react';
import {useLoginBackgroundStyle} from '../utilities/loginBackground';
import { Link, useSearchParams } from 'react-router-dom';
import {useBrandLogo} from '../utilities/genreIcons';
import { RiLockPasswordFill } from 'react-icons/ri';
import Swal from 'sweetalert2';
import './LoginForm.css';

const isPasswordValid = (password) => {
    const passwordPattern = /^(?=(.*[a-z]){3,})(?=(.*[A-Z]){2,})(?=(.*[0-9]){2,})(?=(.*[!@#$%^&*()\-__+.]){1,}).{8,}$/;
    return passwordPattern.test(password);
};

const ResetPassword = () => {
    const loginBgStyle = useLoginBackgroundStyle();
    const ddLogo = useBrandLogo();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(token ? 'form' : 'error'); // form | success | error
    const [message, setMessage] = useState(token ? '' : 'No reset token found. Please use the link from your email.');

    const validatePasswordInput = e => {
        const value = e.target.value;
        if (!value.trim()) {
            setPasswordError('This field is required.');
        } else if (!isPasswordValid(value)) {
            setPasswordError('Password does not meet requirements.');
        } else {
            setPasswordError('');
        }
    };

    const validateConfirmPassword = e => {
        const value = e.target.value;
        if (value !== password) {
            setConfirmPasswordError('Passwords do not match.');
        } else {
            setConfirmPasswordError('');
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!isPasswordValid(password)) {
            setPasswordError('Password does not meet requirements.');
            return;
        }
        if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword: password })
            });
            const data = await res.json();
            if (res.ok) {
                setStatus('success');
                setMessage(data.message || 'Your password has been reset. You can now log in.');
            } else {
                setStatus('error');
                setMessage(data.message || 'This reset link is invalid or has expired.');
            }
        } catch {
            Swal.fire({ timer: 2000, title: 'Error', text: 'Could not reach the server. Please try again later.', icon: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login" style={loginBgStyle}>
            <div className="wrapper" style={status !== 'form' ? { textAlign: 'center' } : undefined}>
                <img src={ddLogo} alt="DD_Logo" className="brand-logo brand-logo--lg"/>
                {status === 'form' && (
                    <form onSubmit={handleSubmit}>
                        <h2 style={{ margin: '0 0 16px', fontSize: '20px', textAlign: 'center' }}>Choose a new password</h2>
                        <div className="input-box">
                            <input
                                type="password"
                                placeholder='New Password'
                                onChange={e => setPassword(e.target.value)}
                                onBlur={validatePasswordInput}
                            />
                            <RiLockPasswordFill className="icon"/>
                            {passwordError && <p>{passwordError}</p>}
                        </div>
                        <div className="input-box">
                            <input
                                type="password"
                                placeholder='Confirm New Password'
                                onChange={e => setConfirmPassword(e.target.value)}
                                onBlur={validateConfirmPassword}
                            />
                            <RiLockPasswordFill className="icon"/>
                            {confirmPasswordError && <p>{confirmPasswordError}</p>}
                        </div>
                        <button type="submit" disabled={submitting}>
                            {submitting ? 'Resetting…' : 'Reset Password'}
                        </button>
                        <div className="register-link" style={{ marginTop: '16px' }}>
                            <p><Link to="/">Log in</Link></p>
                        </div>
                    </form>
                )}
                {status === 'success' && (
                    <>
                        <h2 style={{ margin: '20px 0 10px', fontSize: '22px', color: 'var(--lm-tx-6fcf97)' }}>Password Reset!</h2>
                        <p style={{ fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>{message}</p>
                        <div className="register-link">
                            <p><Link to="/">Log in to DropWyrm</Link></p>
                        </div>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <h2 style={{ margin: '20px 0 10px', fontSize: '22px', color: '#eb5757' }}>Reset Failed</h2>
                        <p style={{ fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>{message}</p>
                        <div className="register-link">
                            <p>Need a new link? <Link to="/forgot-password">Request another one</Link></p>
                        </div>
                    </>
                )}
            </div>
            <div className="copyright-footer">
                &copy; {new Date().getFullYear()} Virtual Realms Studios, LLC. All rights reserved.
            </div>
        </div>
    );
};

export default ResetPassword;
